import { useCallback, useState } from 'react';
import { useFormContext } from '@common-fe/common-fe';

import { PayoutDefinitionFormValues, PayoutOrderCategory } from '../../../PayoutDefinitionSetup.types';

import usePayoutOrderColumns from './usePayoutOrderColumns';
import usePayoutOrderPlans from './usePayoutOrderPlans';

const useRemoveColumnFlow = (categories: PayoutOrderCategory[]) => {
  const [columnToRemove, setColumnToRemove] = useState<number | null>(null);
  const { handleRemoveColumn } = usePayoutOrderColumns();
  const { handleRemoveColumnPlans } = usePayoutOrderPlans();
  const { clearErrors, formState } = useFormContext<PayoutDefinitionFormValues>();

  const columnHasPlans = useCallback(
    (columnIndex: number) =>
      categories.some((category) =>
        category.services.some((service) => service.plans.some((plan) => plan.order === columnIndex))
      ),
    [categories]
  );

  const clearPayoutOrderErrors = useCallback(() => {
    const errorKeys = Object.keys(formState.errors).filter((key) => key.startsWith('service_'));
    if (errorKeys.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clearErrors(errorKeys as any);
    }
  }, [formState.errors, clearErrors]);

  const handleRequestRemoveColumn = useCallback(
    (columnIndex: number) => {
      if (columnHasPlans(columnIndex)) {
        setColumnToRemove(columnIndex);
      } else {
        handleRemoveColumnPlans(columnIndex);
        handleRemoveColumn(columnIndex);
        clearPayoutOrderErrors();
      }
    },
    [columnHasPlans, handleRemoveColumnPlans, handleRemoveColumn, clearPayoutOrderErrors]
  );

  const handleConfirmRemoveColumn = useCallback(() => {
    if (columnToRemove !== null) {
      handleRemoveColumnPlans(columnToRemove);
      handleRemoveColumn(columnToRemove);
      setColumnToRemove(null);
      clearPayoutOrderErrors();
    }
  }, [columnToRemove, handleRemoveColumnPlans, handleRemoveColumn, clearPayoutOrderErrors]);

  const handleCancelRemoveColumn = useCallback(() => {
    setColumnToRemove(null);
  }, []);

  return {
    columnToRemove,
    isRemoveColumnModalVisible: columnToRemove !== null,
    handleRequestRemoveColumn,
    handleConfirmRemoveColumn,
    handleCancelRemoveColumn,
  };
};

export default useRemoveColumnFlow;
